import { Button, Divider, Group, Select, SimpleGrid, Stack, TextInput } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import type { AdPlatform } from '@marketing-tool/shared';
import { Line, Meta } from '@marketing-tool/shared';
import dayjs from 'dayjs';

import type { Campaign } from '../../../data/game-report/types';
import { preventEnterSubmit } from '../../../lib/utils';

export type EditCampaignFormData = {
    id: string | number;
    name?: string;
    startDate?: string;
    endDate?: string;
    configuredStatus?: string;
};

type EditCampaignFormProps = {
    platform: AdPlatform;
    campaign: Campaign;
    onSubmit: (data: EditCampaignFormData) => void;
    onCancel: () => void;
    isSubmitting?: boolean;
};

export const EditCampaignForm = ({ platform, campaign, onSubmit, onCancel, isSubmitting }: EditCampaignFormProps) => {
    const form = useForm<EditCampaignFormData>({
        initialValues: {
            id: campaign.id,
            name: campaign.name,
            startDate: dayjs(campaign.startDate).format('YYYY-MM-DD'),
            endDate: dayjs(campaign.endDate).format('YYYY-MM-DD'),
            configuredStatus: campaign.configuredStatus,
        },
        validate: {
            endDate: (value, values) => {
                if (value && values.startDate && dayjs(value).isBefore(dayjs(values.startDate))) {
                    return 'End date must be after start date';
                }
                return null;
            },
        },
    });

    const handleSubmit = form.onSubmit((values) => {
        // Only send changed fields
        const updates: EditCampaignFormData = { id: campaign.id };
        if (form.isDirty('name')) updates.name = values.name;
        if (form.isDirty('startDate')) updates.startDate = values.startDate;
        if (form.isDirty('endDate')) updates.endDate = values.endDate;
        if (form.isDirty('configuredStatus')) updates.configuredStatus = values.configuredStatus;

        onSubmit(updates);
    });

    const statusOptions =
        platform === 'line'
            ? [
                  { value: Line.ConfiguredStatus.Active, label: 'Active' },
                  { value: Line.ConfiguredStatus.Paused, label: 'Paused' },
                  { value: Line.ConfiguredStatus.Draft, label: 'Draft' },
              ]
            : [
                  { value: Meta.ConfiguredStatus.Active, label: 'Active' },
                  { value: Meta.ConfiguredStatus.Paused, label: 'Paused' },
                  { value: Meta.ConfiguredStatus.Archived, label: 'Archived' },
              ];

    return (
        <form onSubmit={handleSubmit} onKeyDown={preventEnterSubmit}>
            <Stack gap="lg">
                <TextInput
                    label="Campaign Name"
                    placeholder="Enter campaign name"
                    required
                    disabled={isSubmitting}
                    {...form.getInputProps('name')}
                />

                <Select
                    label="Status"
                    required
                    data={statusOptions}
                    disabled={isSubmitting}
                    value={form.values.configuredStatus}
                    onChange={(val) => {
                        if (val !== null) form.setFieldValue('configuredStatus', val);
                    }}
                />

                <SimpleGrid cols={{ base: 1, xs: 2 }} spacing="md">
                    <DatePickerInput
                        label="Start Date"
                        value={form.values.startDate ? dayjs(form.values.startDate).toDate() : null}
                        onChange={(date) => {
                            form.setFieldValue('startDate', date ? dayjs(date).format('YYYY-MM-DD') : '');
                        }}
                        disabled={isSubmitting}
                        required
                        error={form.errors.startDate}
                    />

                    <DatePickerInput
                        label="End Date"
                        value={form.values.endDate ? dayjs(form.values.endDate).toDate() : null}
                        onChange={(date) => {
                            form.setFieldValue('endDate', date ? dayjs(date).format('YYYY-MM-DD') : '');
                        }}
                        disabled={isSubmitting}
                        required
                        error={form.errors.endDate}
                    />
                </SimpleGrid>

                <Divider />
                <Group justify="flex-end" gap="sm">
                    <Button variant="outline" onClick={onCancel} disabled={isSubmitting}>
                        Cancel
                    </Button>
                    <Button type="submit" disabled={isSubmitting}>
                        {isSubmitting ? 'Saving...' : 'Save Changes'}
                    </Button>
                </Group>
            </Stack>
        </form>
    );
};
